import { useState } from 'react';

import { Alert, Box, Button, Chip, Grid, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { format } from 'date-fns';

import {
  CompanyAssessmentType,
  useConfirmFteSizeMutation,
  useFteHistoryQuery,
  useGetCrawlerFindingTokensQuery,
} from '../../../gql/generated/graphql';
import { useSnackbar } from '../../../providers/SnackbarProvider';

interface Props {
  companyId: number;
  crawlResultId: number;
  company: CompanyAssessmentType | null;
  userId: number | null;
}

const SizeStepV2 = ({ companyId, crawlResultId, company, userId }: Props) => {
  const { showSnackbar } = useSnackbar();
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const { data: fteHistoryData, loading: fteHistoryLoading } = useFteHistoryQuery({
    variables: { companyId },
  });

  const { data: findingTokensData } = useGetCrawlerFindingTokensQuery({
    variables: { companyId },
  });

  const [confirmFteSize, { loading: confirmLoading }] = useConfirmFteSizeMutation();

  const analysisFte = company?.fte ?? null;
  const crawlFte = findingTokensData?.getCrawlerFindingTokens?.edges?.[0]?.node?.fte ?? null;
  const fteHistory = fteHistoryData?.fteHistory || [];

  const fteDiffers = analysisFte !== null && crawlFte !== null && analysisFte !== crawlFte;

  const handleConfirm = async (isConfirmedChange: boolean) => {
    if (!userId || crawlFte === null) return;
    const result = await confirmFteSize({
      variables: {
        companyId,
        crawlResultId,
        userId,
        newFte: crawlFte,
        isConfirmedChange,
        notes: notes || undefined,
      },
    });
    if (result.data?.confirmFteSize?.success) {
      setConfirmed(true);
      showSnackbar(isConfirmedChange ? 'FTE change confirmed and updated' : 'FTE change dismissed', 'success');
    } else {
      showSnackbar(result.data?.confirmFteSize?.message || 'Failed', 'error');
    }
  };

  const historyColumns: GridColDef[] = [
    {
      field: 'observedAt',
      headerName: 'Date',
      width: 120,
      valueGetter: value => (value ? format(new Date(value), 'yyyy-MM-dd') : '-'),
    },
    { field: 'fte', headerName: 'FTE', width: 80 },
    {
      field: 'sourceType',
      headerName: 'Source',
      width: 120,
      renderCell: params => <Chip label={params.value || '-'} size="small" variant="outlined" />,
    },
    {
      field: 'isConfirmedChange',
      headerName: 'Confirmed',
      width: 100,
      renderCell: params => {
        if (params.value === true) return <Chip label="Yes" size="small" color="success" />;
        if (params.value === false) return <Chip label="Dismissed" size="small" color="default" />;
        return '-';
      },
    },
    { field: 'notes', headerName: 'Notes', flex: 1, minWidth: 150 },
  ];

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Box p={2} border="1px solid var(--border-subtle)" borderRadius={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Size in DB (Analysis)
            </Typography>
            <Typography variant="h4">{analysisFte ?? 'N/A'}</Typography>
            <Typography variant="caption" color="text.secondary">
              Current analysis FTE
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box p={2} border="1px solid var(--border-subtle)" borderRadius={1}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Crawl FTE
            </Typography>
            <Typography variant="h4">{crawlFte ?? 'N/A'}</Typography>
            <Typography variant="caption" color="text.secondary">
              From latest crawl result
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {fteDiffers && !confirmed && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            FTE changed from {analysisFte} to {crawlFte}. Is this a real change?
          </Typography>
          <Box display="flex" gap={1} mt={1} alignItems="center">
            <TextField
              size="small"
              placeholder="Notes (optional)"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              sx={{ flex: 1, maxWidth: 300 }}
            />
            <Button variant="contained" size="small" onClick={() => handleConfirm(true)} disabled={confirmLoading}>
              Yes, real change
            </Button>
            <Button variant="outlined" size="small" onClick={() => handleConfirm(false)} disabled={confirmLoading}>
              No, dismiss
            </Button>
          </Box>
        </Alert>
      )}

      {confirmed && (
        <Alert severity="success" sx={{ mt: 2 }}>
          FTE confirmation recorded.
        </Alert>
      )}

      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>
          FTE Growth Timeline
        </Typography>
        <DataGrid
          rows={fteHistory}
          getRowId={row => row.id}
          columns={historyColumns}
          loading={fteHistoryLoading}
          autoHeight
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10]}
          sx={{
            fontFamily: 'var(--font-data)',
            fontSize: 'var(--text-sm)',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid var(--border-subtle)',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default SizeStepV2;
